import { apiClient } from '~/lib/apiClient';
import type { WaiverConsent } from '~/services/internalApi/waiver';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshResponse,
  AccountSetupResponse,
  UserProfile,
  ValidateSetupTokenResponse,
  PasswordResetRequestResponse,
  PasswordResetConfirmResponse,
} from '../types';

/**
 * Thrown when login credentials are valid but the account belongs to a
 * different club (tenant) than the one this portal is scoped to. Carries the
 * owning club so the UI can redirect the member to the correct portal.
 */
export class WrongClubError extends Error {
  readonly code = 'ACCOUNT_AT_OTHER_CLUB' as const;
  readonly tenantHandle?: string;
  readonly tenantName?: string;
  readonly clubId?: number;
  constructor(message: string, tenantHandle?: string, tenantName?: string, clubId?: number) {
    super(message);
    this.name = 'WrongClubError';
    this.tenantHandle = tenantHandle;
    this.tenantName = tenantName;
    this.clubId = clubId;
  }
}

/**
 * Thrown when the account exists but was never finished — `pending` (admin-invited) or
 * `incomplete` (self-created in the booking flow). Neither can ever authenticate: both
 * were provisioned with a placeholder password. Carries what the account-setup screen
 * needs so the UI can route the member there instead of dead-ending them on an
 * "invalid credentials" error they can never clear.
 */
export class AccountSetupRequiredError extends Error {
  readonly code = 'ACCOUNT_SETUP_INCOMPLETE' as const;
  readonly userId?: number;
  /** Home club. Absent only for a true orphan (no club enrollments at all). */
  readonly clubId?: number;
  /** True for admin-invited members — the API has just re-sent their setup email. */
  readonly invited: boolean;
  constructor(message: string, userId?: number, clubId?: number, invited = false) {
    super(message);
    this.name = 'AccountSetupRequiredError';
    this.userId = userId;
    this.clubId = clubId;
    this.invited = invited;
  }
}

/**
 * Auth Service
 *
 * Handles all authentication-related API calls using Supabase Auth
 * 
 * ENDPOINTS:
 * - POST /supabase-auth/login - Login user
 * - POST /supabase-auth/register - Register new user
 * - POST /supabase-auth/refresh-token - Refresh access token using refresh token
 * - POST /supabase-auth/logout - Logout user (invalidate tokens)
 * - GET /supabase-auth/me - Get current user profile
 * - POST /supabase-auth/password/reset/request - Request password reset
 * - POST /supabase-auth/password/reset/confirm - Confirm password reset
 */
export const authService = {
  /**
   * Login user
   * 
   * @param credentials - Email and password
   * @returns Login response with user and tokens
   * 
   * @example
   * ```ts
   * const response = await authService.login({
   *   email: 'user@example.com',
   *   password: 'password123'
   * });
   * ```
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const result = await apiClient.post<LoginResponse>('/supabase-auth/login', credentials, {
      skipAuth: true,
    });

    if (!result.success) {
      if (result.error?.code === 'ACCOUNT_SETUP_INCOMPLETE') {
        const details = (result.error.details ?? {}) as {
          userId?: number;
          clubId?: number | null;
          invited?: boolean;
        };
        throw new AccountSetupRequiredError(
          result.message || 'Please finish setting up your account.',
          details.userId,
          details.clubId ?? undefined,
          details.invited ?? false,
        );
      }
      if (result.error?.code === 'ACCOUNT_AT_OTHER_CLUB') {
        const details = (result.error.details ?? {}) as {
          tenantHandle?: string;
          tenantName?: string;
          clubId?: number | null;
        };
        throw new WrongClubError(
          result.message || 'This account belongs to a different club.',
          details.tenantHandle,
          details.tenantName,
          details.clubId ?? undefined,
        );
      }
      throw new Error(result.message || 'Login failed');
    }

    return result.payload;
  },

  /**
   * Register new user
   * 
   * @param userData - User registration data
   * @returns Login response with user and tokens
   * 
   * @example
   * ```ts
   * const response = await authService.register({
   *   email: 'user@example.com',
   *   password: 'password123',
   *   firstName: 'John',
   *   lastName: 'Doe',
   *   phone: '+1234567890'
   * });
   * ```
   */
  async register(userData: RegisterRequest): Promise<LoginResponse> {
    const result = await apiClient.post<LoginResponse>('/supabase-auth/register', userData, {
      skipAuth: true,
    });

    if (!result.success) {
      // Preserve the wire code: SignInModal branches on
      // EXISTING_ACCOUNT_PASSWORD_REQUIRED to re-prompt for the user's existing
      // password (Mode-1 link) instead of dead-ending on prose.
      const err = new Error(result.message || 'Registration failed') as Error & { code?: string };
      err.code = result.error?.code;
      throw err;
    }

    return result.payload;
  },

  /**
   * Refresh access token using refresh token
   * 
   * ✅ IMPORTANT: Supabase automatically rotates refresh tokens on every use
   * Endpoint: POST /supabase-auth/refresh-token
   * Response includes BOTH new access token AND new refresh token
   * 
   * @param refreshToken - Current refresh token
   * @returns Refresh response with new access token AND new refresh token
   * 
   * @example
   * ```ts
   * const response = await authService.refresh(currentRefreshToken);
   * // Store new access token AND new refresh token (both rotated)
   * ```
   */
  async refresh(refreshToken: string): Promise<RefreshResponse> {
    // One shot, never re-entering the refresh:
    // - skipAuthRefresh: a 401 here means the refresh token was refused. Letting the
    //   client's 401 handler call refreshAccessToken() would await this same in-flight
    //   refresh — a deadlock that left page load stuck initializing.
    // - retries: 0: a retry presents the same refresh token again; if the first attempt
    //   rotated it server-side and only the response was lost, a retry >10s later trips
    //   Supabase's reuse detection and revokes the session. The refresh watcher and the
    //   next request are the retry.
    const result = await apiClient.post<RefreshResponse>(
      '/supabase-auth/refresh-token',
      { refresh_token: refreshToken },
      { retries: 0, skipAuthRefresh: true },
    );

    if (!result.success) {
      // Carry the status and code. A bare Error made a 503 (Supabase down) and a 401
      // (refresh token rejected) indistinguishable, so every caller had to assume the
      // worst and wipe the session.
      const error = new Error(
        `${result.httpStatus}: ${result.message || 'Token refresh failed'}`,
      ) as Error & { status?: number; code?: string };
      error.status = result.httpStatus;
      error.code = result.error?.code;
      throw error;
    }

    return result.payload;
  },

  /**
   * Logout user
   * 
   * Invalidates tokens on backend
   * 
   * @param refreshToken - Current refresh token to invalidate
   * 
   * @example
   * ```ts
   * await authService.logout(refreshToken);
   * // Clear local tokens
   * ```
   */
  async logout(refreshToken?: string): Promise<void> {
    try {
      await apiClient.post('/supabase-auth/logout', {
        refresh_token: refreshToken,
      });
    } catch {
      // Swallow — clear local state even if the API call fails.
    }
  },

  /**
   * Get current user profile
   * 
   * Requires valid access token in Authorization header
   * 
   * @returns Current user
   * 
   * @example
   * ```ts
   * const user = await authService.getProfile();
   * ```
   */
  async getProfile(): Promise<UserProfile> {
    const result = await apiClient.get<UserProfile>('/supabase-auth/me');

    if (!result.success) {
      throw new Error(result.message || 'Failed to get profile');
    }

    return result.payload;
  },

  /**
   * Validate account setup token
   * 
   * Validates an invitation token from the account setup email link.
   * Used for the Account Setup page to verify the token before showing the form.
   * Uses the custom token system for user invitations.
   * 
   * @param token - Invitation token from email link
   * @returns Validation result with valid flag and metadata
   * 
   * @example
   * ```ts
   * const result = await authService.validateSetupToken('abc123');
   * if (result.valid) {
   *   // Show account setup form
   * } else {
   *   // Show error: result.error
   * }
   * ```
   */
  async validateSetupToken(token: string): Promise<ValidateSetupTokenResponse> {
    const result = await apiClient.post<ValidateSetupTokenResponse>(
      '/auth/validate-token',
      // On an expired token the API re-sends a fresh invite, resolving the
      // recipient from the token itself — no userId is sent or trusted here.
      { token, purpose: 'invitation' },
      { skipAuth: true }
    );

    // The endpoint envelopes its answer: the validity determination (valid/invalid + reason + the
    // `resent` flag) is the PAYLOAD, because an invalid token is a successful check, not a failure.
    // So success:false only ever means a real transport fault (network/timeout/5xx) — surface a
    // generic message for that and return the payload otherwise.
    if (!result.success) {
      return { valid: false, error: result.message || 'Failed to validate token' };
    }

    return result.payload;
  },

  /**
   * Request password reset
   * 
   * Sends password reset email to user.
   * 
   * @param email - User email
   * @throws Error if request fails
   * 
   * @example
   * ```ts
   * await authService.requestPasswordReset('user@example.com');
   * ```
   */
  async requestPasswordReset(email: string): Promise<void> {
    const result = await apiClient.post<PasswordResetRequestResponse>(
      '/supabase-auth/password/reset/request',
      { email, applicationType: 'web-portal-2' },
      { skipAuth: true }
    );

    if (!result.success) {
      throw new Error(result.message || 'Failed to request password reset');
    }

    // Success - no return value needed
  },

  /**
   * Reset password with token
   * 
   * Confirms password reset using token from email.
   * Returns user email for auto-login after successful reset.
   * 
   * @param token - Password reset token from email
   * @param newPassword - New password
   * @returns Email of the user whose password was reset
   * @throws Error if reset fails
   * 
   * @example
   * ```ts
   * const response = await authService.resetPassword(tokenFromEmail, 'newPassword123');
   * // Use response.email to auto-login
   * ```
   */
  async resetPassword(token: string, newPassword: string): Promise<PasswordResetConfirmResponse> {
    const result = await apiClient.post<PasswordResetConfirmResponse>(
      '/supabase-auth/password/reset/confirm',
      { token, password: newPassword },
      { skipAuth: true }
    );

    if (!result.success) {
      throw new Error(result.message || 'Failed to reset password');
    }

    return result.payload;
  },

  /**
   * Complete account setup for guest user
   * 
   * Upgrades a guest account to a full account with user details and password.
   * This creates a Supabase auth account and updates the user status.
   * 
   * @param userId - ID of the guest user
   * @param userData - User details (email, firstName, lastName, phone, password)
   * @param clubId - Optional club ID to associate
   * @param setupToken - Optional setup token for server-side validation
   * @returns Login response with user and tokens
   * 
   * @example
   * ```ts
   * const response = await authService.completeAccountSetup(
   *   user.id,
   *   {
   *     email: 'user@example.com',
   *     firstName: 'John',
   *     lastName: 'Doe',
   *     phone: '+1234567890',
   *     password: 'securePassword123'
   *   },
   *   1,
   *   'token-from-email'
   * );
   * // User is now authenticated with tokens
   * ```
   */
  async completeAccountSetup(
    userId: string | number,
    userData: {
      email: string;
      firstName: string;
      lastName: string;
      phone: string;
      /** Omitted when the account keeps an existing Butter login (`existingLogin`). */
      password?: string;
      /** Whether the user granted SMS/email consent on the account setup form */
      smsConsent?: boolean;
      /**
       * Waiver consent captured on the setup screen. Recorded server-side in the same
       * transaction as activation, so the account cannot exist without it.
       */
      waiverConsents?: WaiverConsent[];
    },
    clubId?: string | number,
    setupToken?: string
  ): Promise<AccountSetupResponse> {
    const result = await apiClient.post<AccountSetupResponse>(
      '/supabase-auth/complete-account-setup',
      {
        userId,
        ...userData,
        clubId,
        ...(setupToken && { setupToken }), // Only include if provided
      },
      { skipAuth: true } // pre-login flow — no bearer to attach
    );

    if (!result.success) {
      throw new Error(result.message || 'Failed to complete account setup');
    }

    return result.payload;
  },
};
